import { useEffect, useState } from 'react';
import { useInView } from '../../hooks/useInView';
import { apiUrl } from '../../services/apiClient';
import { Certification, fetchCertifications } from './certificationService';
import { CompanyHistoryContent, DEFAULT_COMPANY_HISTORY, fetchCompanyHistory } from './companyHistoryService';
import { Partner, fetchPartners } from './partnerService';
import styles from './CompanyMore.module.scss';

function Pending({ children }: { children: string }) {
  return <p className={styles.pending}>{children}</p>;
}

// 연혁: 회사 소개 + 회사 연혁 + 대표이사 이력 + 디자인사업부 (관리자 콘솔 /admin/company-history 에서 수정)
export function CompanyHistory() {
  const view = useInView<HTMLElement>();
  // 불러오는 동안은 비워 두고, 저장된 내용이 없거나 실패하면 기본 문구를 보여준다
  const [content, setContent] = useState<CompanyHistoryContent | null>(null);

  useEffect(() => {
    fetchCompanyHistory()
      .then((res) => setContent(res.content ?? DEFAULT_COMPANY_HISTORY))
      .catch(() => setContent(DEFAULT_COMPANY_HISTORY));
  }, []);

  // 섹션은 항상 그려야 useInView가 첫 렌더에서 요소를 감시할 수 있다
  return (
    <section ref={view.ref} className={`${styles.section} ${styles.reveal} ${view.inView ? styles.in : ''}`}>
      {content && <CompanyHistoryBody content={content} />}
    </section>
  );
}

function CompanyHistoryBody({ content }: { content: CompanyHistoryContent }) {
  const { intro, history, ceo, services } = content;
  return (
    <div className={styles.inner}>
      <div className={styles.intro}>
        <ul className={styles.badges}>
          {intro.badges.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
        <p>
          {intro.text.map((line, i) => (
            <span key={i}>{line}</span>
          ))}
        </p>
      </div>

      <h2 className={`${styles.title} ${styles.titleGap}`}>회사 연혁</h2>
      {history.length === 0 ? (
        <Pending>연혁 자료를 준비하고 있습니다.</Pending>
      ) : (
        <ol className={styles.timeline}>
          {history.map((g, gi) => (
            <li key={gi}>
              <strong className={styles.year}>{g.label}</strong>
              <ul>
                {g.items.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}

      <h2 className={`${styles.title} ${styles.titleGap}`}>대표이사 이력</h2>
      <div className={styles.ceo}>
        <div className={styles.ceoName}>
          <span>{ceo.title}</span>
          <strong>{ceo.name}</strong>
        </div>
        {ceo.career.length === 0 ? (
          <Pending>대표이사 이력 자료를 준비하고 있습니다.</Pending>
        ) : (
          <div className={styles.careerGroups}>
            {ceo.career.map((g, gi) => (
              <div key={gi}>
                <h3>{g.label}</h3>
                <ul className={styles.career}>
                  {g.items.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <h2 className={`${styles.title} ${styles.titleGap}`}>디자인사업부</h2>
      <ul className={styles.serviceGrid}>
        {services.map((d, i) => (
          <li key={i} className={styles.serviceCard}>
            <strong>{d.title}</strong>
            <span>{d.desc}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// 인증: 관리자 콘솔(/admin/certifications)에서 등록한 인증서 이미지
export function CompanyCertification() {
  const view = useInView<HTMLElement>();
  const [items, setItems] = useState<Certification[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    fetchCertifications()
      .then(setItems)
      .catch(() => setLoadError(true));
  }, []);

  return (
    <section ref={view.ref} className={`${styles.section} ${styles.reveal} ${view.inView ? styles.in : ''}`}>
      <div className={styles.inner}>
        <h2 className={styles.title}>인증 현황</h2>
        {loadError ? (
          <Pending>인증 정보를 불러오지 못했습니다.</Pending>
        ) : items && items.length === 0 ? (
          <Pending>인증 자료를 준비하고 있습니다.</Pending>
        ) : (
          <ul className={styles.certGrid}>
            {items?.map((c) => (
              <li key={c.id} className={styles.certCard}>
                <div className={styles.certImage}>
                  <img src={apiUrl(c.imageUrl)} alt={`${c.title} 인증서`} loading="lazy" />
                </div>
                <strong>{c.title}</strong>
                <span>
                  {c.issuer}
                  {c.issuedDate ? ` · ${c.issuedDate}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// 협력사: 관리자 콘솔(/admin/partners)에서 등록한 협력사
export function CompanyPartners() {
  const view = useInView<HTMLElement>();
  const [items, setItems] = useState<Partner[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    fetchPartners()
      .then(setItems)
      .catch(() => setLoadError(true));
  }, []);

  return (
    <section ref={view.ref} className={`${styles.section} ${styles.reveal} ${view.inView ? styles.in : ''}`}>
      <div className={styles.inner}>
        <h2 className={styles.title}>협력사</h2>
        {loadError ? (
          <Pending>협력사 정보를 불러오지 못했습니다.</Pending>
        ) : items && items.length === 0 ? (
          <Pending>협력사 소개 자료를 준비하고 있습니다.</Pending>
        ) : (
          <ul className={styles.partnerGrid}>
            {items?.map((p) => {
              const body = (
                <>
                  <div className={styles.partnerLogo}>
                    {p.logoUrl ? <img src={apiUrl(p.logoUrl)} alt={p.name} loading="lazy" /> : p.name}
                  </div>
                  <strong>{p.name}</strong>
                  {p.field && <span>{p.field}</span>}
                </>
              );
              return (
                <li key={p.id} className={styles.partnerCard}>
                  {p.url ? (
                    <a href={p.url} target="_blank" rel="noopener noreferrer">
                      {body}
                    </a>
                  ) : (
                    body
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
